import { Link, useSearchParams } from "react-router-dom";

import { useRecipes } from "../api/queries";
import { SCROLLS_OFF, countsScrolls } from "../api/recipeQuery";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { adena, signed } from "../format";
import { itemHref, recipeHref } from "../search/nav";
import type { CostMode, RecipeSort } from "./recipeSort";
import { costOf, filterRecipes, marginOf, pricedOf, sortRecipes } from "./recipeSort";

const SORTS: RecipeSort[] = ["margin", "item", "grade", "cost", "level"];
const SORT_LABELS: Record<RecipeSort, string> = {
  margin: "margin", item: "item", grade: "grade", cost: "cost", level: "level",
};

/** What it costs to make each of the 995 recipes, and what it sells for.
 *
 *  The controls live in the query string rather than in state, so a sorted,
 *  filtered view is a link you can send someone — the same reason the scan
 *  pages carry their uid in the path. */
export function Recipes() {
  const [params, setParams] = useSearchParams();
  const mode: CostMode = params.get("mode") === "buy" ? "buy" : "craft";
  const sort = (SORTS.find((s) => s === params.get("sort")) ?? "margin") as RecipeSort;
  const filter = params.get("q") ?? "";
  // Checked by default, and a checked box writes nothing: a craft consumes the
  // recipe on this server, so counting the scroll is the accurate reading and
  // the link a reader would follow cold should already be it.
  const scrolls = countsScrolls(params);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  // The one control here that is a refetch rather than a render: it reshapes
  // the ingredient set the costs are summed from, so the server has to sum
  // them again. Both readings cache under their own key, so coming back is
  // instant.
  const recipes = useRecipes(scrolls);

  if (recipes.isLoading) return <><TitleBand title="Recipes" /><Skeleton rows={8} /></>;
  if (recipes.isError) {
    return (
      <>
        <TitleBand title="Recipes" />
        <ErrorBand
          message="Could not load the recipes."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }

  const all = recipes.data?.recipes ?? [];
  const rows = sortRecipes(filterRecipes(all, filter), sort, mode);
  const complete = all.filter((row) => costOf(row, mode) !== null).length;

  return (
    <>
      <TitleBand
        title="Recipes"
        note={`${all.length} recipes · ${complete} fully priced · scraped ${recipes.data?.scraped_at}`}
      />

      <Panel primary>
        {/* Three groups of controls, separated by a rule rather than by an
            empty span borrowing one extra `gap` — which is what used to
            separate the first two, invisibly enough that it was deleted as
            inert. What the row means is that these are three kinds of thing:
            which cost to read, how to order the table, and what to count. */}
        <div className="strip">
          <div className="group">
            {/* Two readings of one catalogue. Both totals are already in hand,
                so this is a render rather than a refetch. */}
            <button
              type="button"
              aria-pressed={mode === "buy"}
              onClick={() => set("mode", mode === "buy" ? "" : "buy")}
            >
              market prices only
            </button>
            <button
              type="button"
              aria-pressed={mode === "craft"}
              onClick={() => set("mode", "craft")}
            >
              prices + crafting
            </button>
          </div>
          <div className="group">
            {SORTS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={sort === option}
                onClick={() => set("sort", option)}
              >
                {SORT_LABELS[option]}
              </button>
            ))}
          </div>
          <div className="group">
            <label className="check">
              <input
                type="checkbox"
                checked={scrolls}
                onChange={() => set("scrolls", scrolls ? SCROLLS_OFF : "")}
              />
              <span>count recipe scrolls</span>
            </label>
          </div>
          <input
            type="search"
            value={filter}
            placeholder="filter by item"
            aria-label="filter recipes by item"
            onChange={(event) => set("q", event.target.value)}
          />
        </div>

        {rows.length === 0 ? (
          <Empty>
            {filter ? "No recipe matches that." : "No recipes in the catalogue."}
          </Empty>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Grade</th>
                  <th className="num">Lv</th>
                  <th className="num">Success</th>
                  <th className="num">Materials</th>
                  <th className="num">Cost</th>
                  <th className="num">Sells for</th>
                  <th className="num">Buys for</th>
                  <th className="num">Margin</th>
                  <th className="num">Crafter fee</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const margin = marginOf(row, mode);
                  return (
                    <tr key={row.id}>
                      <td>
                        <Link to={recipeHref(row.id)}>{row.craft_item}</Link>
                        {row.count > 1 ? <span className="dim"> ×{row.count}</span> : null}
                        {row.craft_item_id === null ? null : (
                          <>
                            {" "}
                            <Link className="dim" to={itemHref(row.craft_item_id, row.craft_item)}>
                              market
                            </Link>
                          </>
                        )}
                      </td>
                      <td className="dim">{row.grade}</td>
                      <td className="num dim">{row.craft_level}</td>
                      {/* A rate under 100 is a fact about the recipe, not a
                          multiplier on its cost: the material counts are
                          already balanced against it. */}
                      <td className="num dim">{row.success}%</td>
                      <td className="num dim">
                        {pricedOf(row, mode)}/{row.lines}
                      </td>
                      <td className="num adena">{adena(costOf(row, mode))}</td>
                      <td className="num sell">{adena(row.sells_for)}</td>
                      <td className="num buy">{adena(row.buys_for)}</td>
                      {/* The sign is a character as well as a colour: colour is
                          never the only signal here. */}
                      <td className={`num ${margin === null ? "dim" : margin < 0 ? "buy" : "sell"}`}>
                        {signed(margin)}
                      </td>
                      <td className="num dim">{adena(row.craft_fee)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
